import { useContext, createContext } from 'react';
import { types, Instance, onSnapshot } from 'mobx-state-tree';
import UserModel from 'models/user-model';
import position from './position';
import modal from './modal';
import storage from '../lib/storage';

const initState = () => {
  return {
    position: position.initState,
    modal: modal.initState,
  };
};

const GlobalModel = types
  .model({
    position: position.Model,
    modal: modal.Model,
    pageClass: '',
    // isLogin: false,
    userInfo: types.maybeNull(UserModel),
    serverTime: 0,
    timeOffset: 0,
    isUserInfoReady: false,
    isLibJsReady: false,
    isSocketReady: false,
    isActReady: false,
    isQtClientReady: false,
    isGlobalPageConfigReady: false,
    screen: 'sm',
    clientFontMode: storage.getItem('clientFontMode') || 'base',
    isDark: false,
    isLoginSuccess: false, // 用作登录操作时  登录成功标识
    pageType: '',
    browserType: '', // pc_client 开播工具Electron，pc_web 网页版

    unreadCount: 0, // im 私聊未读数
  })
  .views((self) => {
    return {
      get isLogin() {
        return !!self.userInfo;
      },
      get userId() {
        return self.userInfo && self.userInfo._id;
      },
      get clientFontMap() {
        return {
          [self.clientFontMode]: true,
        };
      },
      get isPcClient() {
        return self.browserType === 'pc_client';
      },
      get isPcWeb() {
        return self.browserType === 'pc_web';
      },
    };
  })
  .actions((self) => {
    const setServerTime = (time) => {
      self.timeOffset = time - Date.now();
      self.serverTime = time;
    };

    const setPageClass = (className) => {
      self.pageClass = className;
    };
    const setDark = (status) => {
      self.isDark = status;
    };
    const setUserInfo = (user) => {
      self.isUserInfoReady = true;
      self.userInfo = user;
    };
    const setUserInfoReady = () => {
      self.isUserInfoReady = true;
    };
    const setLibJsReady = () => {
      self.isLibJsReady = true;
    };
    const setSocketReady = (status) => {
      self.isSocketReady = status;
    };
    const setScreen = (size) => {
      self.screen = size;
    };
    const setActReady = (status) => {
      self.isActReady = status;
    };
    const setQtClientReady = (status) => {
      self.isQtClientReady = status;
    };
    // 服务器的当前时间
    const getNow = () => {
      return self.timeOffset + Date.now();
    };

    const setLoginSuccess = () => {
      self.isLoginSuccess = true;
    };

    const setPageType = (type) => {
      self.pageType = type;
    };

    const setGlobalPageConfigReady = (status) => {
      self.isGlobalPageConfigReady = status;
    };

    const setClientFontMode = (mode) => {
      storage.setItem('clientFontMode', mode);
      self.clientFontMode = mode;
    };

    const setUnreadCount = (count) => {
      self.unreadCount = count;
    };

    const setBrowserType = (type: string) => {
      self.browserType = type;
    };

    return {
      setPageClass,
      setUserInfo,
      setServerTime,
      setUserInfoReady,
      setLibJsReady,
      setSocketReady,
      setActReady,
      setQtClientReady,
      setScreen,
      getNow,
      setDark,
      setLoginSuccess,
      setPageType,
      setGlobalPageConfigReady,
      setClientFontMode,
      setUnreadCount,
      setBrowserType,
    };
  });

export const globalStore = GlobalModel.create(initState());

if (typeof window !== 'undefined') {
  window.__globalStore = globalStore;
}

// onSnapshot(globalStore, snapshot => console.log("Root Snapshot: ", snapshot));

export type RootInstance = Instance<typeof GlobalModel>;
const RootStoreContext = createContext<null | RootInstance>(null);

export const Provider = RootStoreContext.Provider;

export function useStore() {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider');
  }
  return store;
}

const getGlobalStore = () => {
  return globalStore;
};

export { useStore as useGlobalStore, globalStore as rootStore, getGlobalStore };
